import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { adminApi, errMsg, LOGO } from "@/lib/api";
import { useSite } from "@/lib/store";
import { SOCIALS } from "@/components/store/Chrome";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Btn, Loading, PageHead, Panel, SaveBtn, inputCls, useSiteDraft } from "@/admin/ui";

const full = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : LOGO);

function ImageSlot({ kind, label, hint, url }) {
  const { reload } = useSite();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await adminApi.post(`/branding/${kind}`, fd);
      await reload();
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  const reset = async () => {
    try {
      await adminApi.delete(`/branding/${kind}`);
      await reload();
      toast.success(`${label} reset to the original`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel className="flex flex-wrap items-center gap-6" data-testid={`branding-${kind}`}>
      <div className={`grid place-items-center bg-zinc-50 border border-zinc-200 ${kind === "logo" ? "w-56 h-24 p-3" : "w-20 h-20 p-3"}`}>
        <img src={full(url)} alt={label} className="max-w-full max-h-full object-contain" data-testid={`branding-${kind}-preview`} />
      </div>
      <div className="flex-1 min-w-[220px]">
        <p className="font-bold text-sm">{label}</p>
        <p className="text-xs text-zinc-500 mt-1">{hint}</p>
        <div className="flex gap-2 mt-3">
          <label className={`inline-flex items-center gap-2 h-9 px-4 text-xs font-bold bg-black text-white cursor-pointer hover:bg-red transition-colors ${busy ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload new"}
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,.ico" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`branding-${kind}-input`} />
          </label>
          {url && <Btn variant="outline" className="h-9 px-4" onClick={reset} data-testid={`branding-${kind}-reset`}>Use original</Btn>}
        </div>
      </div>
    </Panel>
  );
}

function Socials({ rows, onChange }) {
  const set = (i, k, v) => onChange(rows.map((r, n) => (n === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3" data-testid="branding-socials">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-center" data-testid={`branding-social-row-${i}`}>
          <Select value={r.platform} onValueChange={(v) => set(i, "platform", v)}>
            <SelectTrigger className="w-44 rounded-none bg-white h-10" data-testid={`branding-social-platform-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">{Object.entries(SOCIALS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <input value={r.url} onChange={(e) => set(i, "url", e.target.value)} placeholder="https://…" className={`${inputCls} flex-1`} data-testid={`branding-social-url-${i}`} />
          <button onClick={() => onChange(rows.filter((_, n) => n !== i))} className="text-zinc-400 hover:text-red p-2" aria-label="Remove" data-testid={`branding-social-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <Btn variant="outline" className="h-9 px-4" onClick={() => onChange([...rows, { platform: "youtube", url: "" }])} data-testid="branding-social-add"><Plus className="w-4 h-4" /> Add social link</Btn>
    </div>
  );
}

export default function Branding() {
  const { site } = useSite();
  const { draft, patch, save, saving } = useSiteDraft(["socials"]);
  if (!draft || !site) return <Loading />;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHead title="Logo, favicon & social" desc="Change the logo and browser-tab icon, and choose which social icons show in the footer." />
      <ImageSlot kind="logo" label="Logo" hint="Shown in the header, footer, menu and emails. PNG, JPG, WebP or SVG, under 1 MB. A wide image with a transparent or white background works best." url={site.logo_url} />
      <ImageSlot kind="favicon" label="Favicon" hint="The small icon in the browser tab. A square PNG or ICO (e.g. 64×64 or 512×512), under 1 MB." url={site.favicon_url} />
      <Panel className="space-y-4">
        <div className="flex justify-between items-center gap-3">
          <p className="font-bold text-sm">Social icons in the footer</p>
          <SaveBtn save={save} saving={saving} />
        </div>
        <Socials rows={draft.socials || []} onChange={(v) => patch("socials", v)} />
      </Panel>
    </div>
  );
}
